// src/pages/ResetPassword.jsx
import React, { useState, useEffect } from 'react';
import { useSearchParams, useNavigate } from 'react-router-dom';
import { apiFetchJSON } from '../api/apiClient';
import logo from '../logo512.png';
import '../styles/Verification.css';

// Matches the mockup's own hand-drawn eye icon exactly, same as Login.
const EyeIcon = () => (
  <svg width="17" height="17" viewBox="0 0 20 20" fill="none" stroke="#33565C" strokeWidth="1.5">
    <path d="M1.8 10S4.9 4.6 10 4.6 18.2 10 18.2 10 15.1 15.4 10 15.4 1.8 10 1.8 10z"></path>
    <circle cx="10" cy="10" r="2.4"></circle>
  </svg>
);

// Matches 6d's exact 4-requirement checklist + 4-segment strength bar.
function checkPasswordRules(password) {
  return {
    length: password.length >= 12,
    case: /[a-z]/.test(password) && /[A-Z]/.test(password),
    number: /[0-9]/.test(password),
    symbol: /[^A-Za-z0-9]/.test(password),
  };
}

function PasswordStrength({ password }) {
  const rules = checkPasswordRules(password);
  const passedCount = Object.values(rules).filter(Boolean).length;

  return (
    <>
      <div className="pw-strength-bar">
        {[0, 1, 2, 3].map((i) => (
          <span key={i} className={`pw-strength-seg ${i < passedCount ? 'filled' : ''}`} />
        ))}
      </div>
      <div className="pw-rules-row">
        <span className={`pw-rule ${rules.length ? 'met' : ''}`}>
          <span className="pw-rule-check">{rules.length ? '✓' : ''}</span>12+ characters
        </span>
        <span className={`pw-rule ${rules.case ? 'met' : ''}`}>
          <span className="pw-rule-check">{rules.case ? '✓' : ''}</span>Upper &amp; lowercase
        </span>
        <span className={`pw-rule ${rules.number ? 'met' : ''}`}>
          <span className="pw-rule-check">{rules.number ? '✓' : ''}</span>A number
        </span>
        <span className={`pw-rule ${rules.symbol ? 'met' : ''}`}>
          <span className="pw-rule-check">{rules.symbol ? '✓' : ''}</span>A symbol
        </span>
      </div>
    </>
  );
}

function ResetPassword() {
    const [searchParams] = useSearchParams();
    const navigate = useNavigate();
    const token = searchParams.get('token');
    
    const [password, setPassword] = useState('');
    const [confirmPassword, setConfirmPassword] = useState('');
    const [showPassword, setShowPassword] = useState(false);
    const [showConfirmPassword, setShowConfirmPassword] = useState(false);
    const [loading, setLoading] = useState(true);
    const [resetting, setResetting] = useState(false);
    const [error, setError] = useState('');
    const [success, setSuccess] = useState(false);
    const [tokenValid, setTokenValid] = useState(false);
    // Account has an authenticator app -> the emailed link alone isn't enough
    const [totpRequired, setTotpRequired] = useState(false);
    const [totpCode, setTotpCode] = useState('');

    useEffect(() => {
        const verifyToken = async () => {
            try {
                setLoading(true);
                const response = await apiFetchJSON(`/api/verify/check-token?token=${token}`);
                if (response.valid && ['password_reset', 'admin_password_reset'].includes(response.token_type)) {
                    setTokenValid(true);
                    setTotpRequired(!!response.totp_required);
                } else {
                    setError('Invalid or expired password reset link');
                }
            } catch (err) {
                setError(err.message || 'Failed to verify reset link');
            } finally {
                setLoading(false);
            }
        };
        
        if (token) {
            verifyToken();
        } else {
            setError('No reset token provided');
            setLoading(false);
        }
    }, [token]);

    const passwordRules = checkPasswordRules(password);
    const passwordValid = Object.values(passwordRules).every(Boolean);
    const passwordsMatch = password && confirmPassword && password === confirmPassword;

    const handleSubmit = async (e) => {
        e.preventDefault();
        
        if (password !== confirmPassword) {
            setError('Passwords do not match');
            return;
        }
        
        if (!passwordValid) {
            setError('Password does not meet the requirements below');
            return;
        }
        
        try {
            setResetting(true);
            setError('');
            
            const response = await apiFetchJSON('/api/reset-password', {
                method: 'POST',
                body: JSON.stringify(totpRequired ? { token, password, totp_code: totpCode } : { token, password })
            });
            
            if (response.success) {
                setSuccess(true);
                setTimeout(() => navigate('/login'), 3000);
            } else {
                setError(response.error || 'Password reset failed');
            }
        } catch (err) {
            setError(err.message || 'Password reset failed');
        } finally {
            setResetting(false);
        }
    };

    if (loading) {
        return (
            <div className="login-page">
                <div className="login-container">
                    <div className="login-header">
                        <img src={logo} className="login-logo-247" alt="247 Logo" />
                        <div><h2>Verifying reset link...</h2></div>
                    </div>
                    <div className="verification-loading">
                        <span className="spinner-icon">↻</span>
                        <p>Please wait...</p>
                    </div>
                </div>
            </div>
        );
    }

    if (error && !success && !tokenValid) {
        return (
            <div className="login-page">
                <div className="login-container">
                    <div className="login-header">
                        <img src={logo} className="login-logo-247" alt="247 Logo" />
                        <div><h2>Reset Link Invalid</h2></div>
                    </div>
                    
                    <div className="login-error">
                        <span className="login-error-icon">!</span>
                        {error}
                    </div>
                    
                    <button 
                        className="login-btn"
                        onClick={() => navigate('/login')}
                    >
                        Go to Login
                    </button>
                    
                    <div className="login-info">
                        <p>Contact your administrator for a new reset link</p>
                    </div>
                </div>
            </div>
        );
    }

    if (success) {
        return (
            <div className="login-page">
                <div className="login-container">
                    <div className="login-header">
                        <img src={logo} className="login-logo-247" alt="247 Logo" />
                        <div><h2>Password Reset Complete!</h2></div>
                    </div>
                    
                    <div className="verification-success">
                        <svg
                            className="verification-success-icon"
                            viewBox="0 0 24 24"
                            fill="none"
                            xmlns="http://www.w3.org/2000/svg"
                            aria-hidden="true"
                        >
                            <path
                                d="M5 12.5L9.5 17L19 7.5"
                                stroke="currentColor"
                                strokeWidth="2.5"
                                strokeLinecap="round"
                                strokeLinejoin="round"
                            />
                        </svg>
                        <p>Your password has been successfully reset.</p>
                        <p className="redirect-note">Redirecting you to login in 3 seconds...</p>
                    </div>
                </div>
            </div>
        );
    }

    return (
        <div className="login-page">
            <div className="login-container verification-container">
                <div className="login-header">
                    <img src={logo} className="login-logo-247" alt="247 Logo" />
                    <div>
                        <h2 className="verification-title">Reset your password</h2>
                        <p>Enter your new password below</p>
                    </div>
                </div>
                
                {error && (
                    <div className="login-error">
                        <span className="login-error-icon">!</span>
                        {error}
                    </div>
                )}
                
                <form onSubmit={handleSubmit}>
                    <div className="form-group password-group">
                        <label htmlFor="password">New Password</label>
                        <div className="password-input-wrapper">
                            <input
                                type={showPassword ? "text" : "password"}
                                id="password"
                                value={password}
                                onChange={(e) => setPassword(e.target.value)}
                                required
                                disabled={resetting}
                                autoFocus
                            />
                            <button 
                                type="button" 
                                className="password-toggle"
                                onClick={() => setShowPassword(!showPassword)}
                                disabled={resetting}
                            >
                                <EyeIcon />
                            </button>
                        </div>
                        <PasswordStrength password={password} />
                    </div>
                    
                    <div className="form-group password-group">
                        <label htmlFor="confirmPassword">Confirm New Password</label>
                        <div className="password-input-wrapper">
                            <input
                                type={showConfirmPassword ? "text" : "password"}
                                id="confirmPassword"
                                value={confirmPassword}
                                onChange={(e) => setConfirmPassword(e.target.value)}
                                required
                                disabled={resetting}
                            />
                            {passwordsMatch && <span className="pw-match-indicator">MATCH ✓</span>}
                            <button 
                                type="button" 
                                className="password-toggle"
                                onClick={() => setShowConfirmPassword(!showConfirmPassword)}
                                disabled={resetting}
                            >
                                <EyeIcon />
                            </button>
                        </div>
                        {password && confirmPassword && !passwordsMatch && (
                            <div className="pw-mismatch">Passwords do not match</div>
                        )}
                    </div>
                    
                    {totpRequired && (
                        <div className="form-group">
                            <label htmlFor="totpCode">Authenticator code</label>
                            <input
                                id="totpCode"
                                type="text"
                                inputMode="text"
                                autoComplete="one-time-code"
                                autoCapitalize="characters"
                                placeholder="123456 or backup code"
                                maxLength={9}
                                value={totpCode}
                                onChange={(e) => setTotpCode(e.target.value.toUpperCase().replace(/[^A-Z0-9-]/g, ''))}
                                disabled={resetting}
                            />
                        </div>
                    )}

                    <button 
                        type="submit" 
                        className="login-btn"
                        disabled={resetting || !passwordValid || !passwordsMatch || (totpRequired && totpCode.replace(/-/g, '').length < 6)}
                    >
                        {resetting ? (
                            <>
                                <span className="spinner-icon">↻</span>
                                Resetting...
                            </>
                        ) : (
                            'Reset Password'
                        )}
                    </button>
                </form>
                
                <div className="login-info">
                    <p>This link expires in 24 hours</p>
                </div>
            </div>
        </div>
    );
}

export default ResetPassword;
